import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AppLayout } from "@/components/AppLayout";

export default function Blog() {
  const categories = [
    {
      title: "Resume Tailoring Tips",
      description: "Learn how to match your resume to the role using data-driven strategies.",
      icon: "📝"
    },
    {
      title: "ATS & Hiring Trends", 
      description: "Stay up to date on how applicant tracking systems evaluate resumes—and how to beat the filters.",
      icon: "🤖"
    },
    {
      title: "LinkedIn Optimization",
      description: "Boost your visibility and attract opportunities without constantly applying.",
      icon: "💼"
    },
    {
      title: "Career Pivots & Transitions",
      description: "Explore new directions, industries, and opportunities—without starting over.",
      icon: "🚀"
    },
    {
      title: "Job Search Systems",
      description: "Build a repeatable, daily job search routine that gets results.",
      icon: "⚡"
    }
  ];

  const recentPosts = [
    "Why Tailored Resumes Get 4x More Interviews Than Generic Ones",
    "The 6-Sentence Resume Summary That Gets Attention", 
    "How to Tell If You're Qualified for a Job—Before You Apply",
    "LinkedIn vs. Resume: What to Say on Each and Why It Matters"
  ];

  return (
    <AppLayout currentPage="blog">
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 pt-8 pb-8 max-w-4xl">
          <h1 className="text-4xl font-bold text-foreground mb-4">TailorWiz Insights</h1>
          <p className="text-xl text-muted-foreground">
            Practical advice, proven job search strategies, and real talk about what works.
          </p>
        </div>
        
        <main className="container mx-auto px-4 py-8 max-w-6xl">
          <div className="mb-12">
            <p className="text-lg text-muted-foreground leading-relaxed max-w-3xl">
              Welcome to the TailorWiz Blog—your hub for smarter job search tips, resume insights, and career growth content. Every article is written with one goal: to help you land interviews faster and make career moves with confidence.
            </p>
          </div>

          <section className="mb-16">
            <div className="flex items-center gap-2 mb-8">
              <span className="text-2xl">📚</span>
              <h2 className="text-3xl font-bold text-foreground">Popular Categories</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {categories.map((category, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow cursor-pointer">
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{category.icon}</span>
                      <CardTitle className="text-lg">{category.title}</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-sm leading-relaxed">
                      {category.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          <section className="mb-16">
            <div className="flex items-center gap-2 mb-8">
              <span className="text-2xl">🔥</span>
              <h2 className="text-3xl font-bold text-foreground">Recent Posts</h2>
            </div>
            
            <div className="space-y-4">
              {recentPosts.map((post, index) => (
                <Card key={index} className="hover:shadow-md transition-shadow cursor-pointer">
                  <CardHeader className="pb-4">
                    <div className="flex items-start gap-3">
                      <Badge variant="secondary" className="bg-primary/10 text-primary">
                        ✅
                      </Badge>
                      <CardTitle className="text-lg leading-tight hover:text-primary transition-colors">
                        {post}
                      </CardTitle>
                    </div>
                  </CardHeader>
                </Card>
              ))}
            </div>
          </section>

          <section className="mb-16">
            <Card className="bg-primary/5 border-primary/20">
              <CardHeader className="text-center">
                <CardTitle className="text-2xl text-primary">Stay Updated</CardTitle>
                <CardDescription className="text-lg">
                  New articles published weekly. Want content delivered straight to your inbox?
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Input 
                  placeholder="Enter your email address" 
                  className="max-w-sm"
                  type="email"
                />
                <Button size="lg" className="whitespace-nowrap">
                  Subscribe to Newsletter
                </Button>
              </CardContent>
            </Card>
          </section>
        </main>
      </div>
    </AppLayout>
  );
}
